import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../utils/apiFetch";
import {
  cancelAdminDeal,
  deleteAdminDeal,
  resetAdminDeal,
} from "./dealApi";

import "./global/global.css";

const STATUS_LABELS = {
  document_creation: "Doc Creation",
  tracking: "Tracking",
  closed: "Closed",
  cancelled: "Cancelled",
};

const PROGRAM_STATUS_OPTIONS = [
  "program_executing",
  "program_complete",
  "awaiting_confirmation",
  "awaiting_document_creation",
  "on_hold",
];

const STATUS_CLASS = {
  document_creation: "status-pending",
  tracking: "status-sent",
  closed: "status-seen",
  cancelled: "status-failed",
};

const AdminDealList = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [filterAddress, setFilterAddress] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterProgramStatus, setFilterProgramStatus] = useState("");
  const [filterRealtor, setFilterRealtor] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());

  const apiBase = `${process.env.REACT_APP_PAYVIEW_BASE_URL}/api/admin`;

  useEffect(() => {
    fetchDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uniqueRealtors = useMemo(() => {
    const emails = deals.map((d) => d.realtor_email).filter(Boolean);
    return [...new Set(emails)].sort();
  }, [deals]);

  const fetchDeals = async () => {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    try {
      const res = await apiFetch(`${apiBase}/deals/`);
      if (!res.ok) throw new Error("Failed to fetch deals");
      const data = await res.json();
      setDeals(data.deals || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (deal) => {
    if (
      !window.confirm(
        `Cancel deal at "${deal.property_address}"? This will halt all pending milestones.`
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await cancelAdminDeal(deal.id);
      setDeals((prev) =>
        prev.map((d) => (d.id === deal.id ? { ...d, status: "cancelled" } : d))
      );
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleDelete = async (deal) => {
    if (
      !window.confirm(
        `Permanently delete deal at "${deal.property_address}" and all associated data? This cannot be undone.`
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await deleteAdminDeal(deal.id);
      setDeals((prev) => prev.filter((d) => d.id !== deal.id));
    } catch (err) {
      setActionError(err.message);
    }
  };

  const isResetEligible = (deal) =>
    ["executing", "plan_proposed"].includes(deal.program_status);

  const handleReset = async (deal) => {
    if (
      !window.confirm(
        `Reset deal at "${deal.property_address}" to "Plan Proposed"? The realtor's next reply will re-enter the plan-confirmation flow.`
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await resetAdminDeal(deal.id);
      setDeals((prev) =>
        prev.map((d) =>
          d.id === deal.id ? { ...d, program_status: "plan_proposed" } : d
        )
      );
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleBulkCancel = async () => {
    const count = selectedIds.size;
    if (!window.confirm(`Cancel ${count} selected deal(s)? This will halt all pending milestones.`)) {
      return;
    }
    setActionError(null);
    try {
      for (const id of selectedIds) {
        const deal = deals.find((d) => d.id === id);
        if (!deal || deal.status === "cancelled" || deal.status === "closed") continue;
        const res = await apiFetch(`${apiBase}/deals/${id}/cancel/`, { method: "POST" });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || `Failed to cancel deal ${id}`);
        }
        setDeals((prev) => prev.map((d) => (d.id === id ? { ...d, status: "cancelled" } : d)));
      }
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    if (!window.confirm(`Permanently delete ${count} selected deal(s)? This cannot be undone.`)) {
      return;
    }
    setActionError(null);
    try {
      for (const id of selectedIds) {
        const res = await apiFetch(`${apiBase}/deals/${id}/`, { method: "DELETE" });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || `Failed to delete deal ${id}`);
        }
        setDeals((prev) => prev.filter((d) => d.id !== id));
      }
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSelectedIds(new Set());
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const filteredDeals = deals.filter((deal) => {
    if (filterAddress && !(deal.property_address || "").toLowerCase().includes(filterAddress.toLowerCase())) {
      return false;
    }
    if (filterStatus && deal.status !== filterStatus) {
      return false;
    }
    if (filterProgramStatus && deal.program_status !== filterProgramStatus) {
      return false;
    }
    if (filterRealtor && deal.realtor_email !== filterRealtor) {
      return false;
    }
    return true;
  });

  const hasActiveFilter = filterAddress || filterStatus || filterProgramStatus || filterRealtor;
  const subtitle = hasActiveFilter
    ? `Showing ${filteredDeals.length} of ${deals.length} deal${deals.length !== 1 ? "s" : ""}`
    : `${deals.length} deal${deals.length !== 1 ? "s" : ""} across all realtors`;

  if (loading) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          <div className="client-list-heading" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
            <div className="lc">
              <h3 className="cl-rh">All Deals</h3>
              <p className="cl-rh-sub">All deals across all realtors.</p>
            </div>
          </div>
          <div className="cl-table-container">
            <p className="loading">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="client-list-root">
        <div className="client-list-container">
          <div className="client-list-heading" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
            <div className="lc">
              <h3 className="cl-rh">All Deals</h3>
              <p className="cl-rh-sub">All deals across all realtors.</p>
            </div>
          </div>
          <div className="cl-table-container">
            <p className="error">Error: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="client-list-root">
      <div className="client-list-container">
        <div className="client-list-heading" style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "16px" }}>
          <div className="lc">
            <h3 className="cl-rh">All Deals</h3>
            <p className="cl-rh-sub">{subtitle}</p>
          </div>
        </div>

        {actionError && (
          <div style={{
            margin: "16px 20px 0",
            padding: "10px 14px",
            backgroundColor: "#fce4ec",
            color: "#b71c1c",
            borderRadius: "6px",
            fontSize: "14px",
          }}>
            {actionError}
          </div>
        )}

        {/* Filter bar */}
        <div style={{
          display: "flex",
          gap: "12px",
          padding: "16px 20px 0",
          flexWrap: "wrap",
          alignItems: "center",
        }}>
          <input
            type="text"
            placeholder="Filter by address..."
            value={filterAddress}
            onChange={(e) => setFilterAddress(e.target.value)}
            style={filterInputStyle}
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={filterInputStyle}
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={filterProgramStatus}
            onChange={(e) => setFilterProgramStatus(e.target.value)}
            style={filterInputStyle}
          >
            <option value="">All Program Statuses</option>
            {PROGRAM_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>
          <select
            value={filterRealtor}
            onChange={(e) => setFilterRealtor(e.target.value)}
            style={filterInputStyle}
          >
            <option value="">All Realtors</option>
            {uniqueRealtors.map((email) => (
              <option key={email} value={email}>{email}</option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "10px 20px",
            marginTop: "12px",
            backgroundColor: "#e8f0fe",
            borderTop: "1px solid #c5cae9",
            borderBottom: "1px solid #c5cae9",
            fontSize: "14px",
          }}>
            <span style={{ fontWeight: 500, color: "#3949ab" }}>
              {selectedIds.size} deal(s) selected
            </span>
            <button
              onClick={handleBulkCancel}
              style={{
                padding: "4px 12px",
                fontSize: "13px",
                borderRadius: "4px",
                border: "1px solid #bbb",
                background: "#fff",
                color: "#555",
                cursor: "pointer",
              }}
            >
              Cancel Selected
            </button>
            <button
              onClick={handleBulkDelete}
              style={{
                padding: "4px 12px",
                fontSize: "13px",
                borderRadius: "4px",
                border: "1px solid #e57373",
                background: "#fff",
                color: "#c62828",
                cursor: "pointer",
              }}
            >
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              style={{
                padding: "4px 8px",
                fontSize: "13px",
                border: "none",
                background: "none",
                color: "#3949ab",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Clear selection
            </button>
          </div>
        )}

        <div className="cl-table-container">
          {filteredDeals.length === 0 ? (
            <p style={{ color: "#777", fontSize: "14px" }}>
              {deals.length === 0 ? "No deals found." : "No deals match the current filters."}
            </p>
          ) : (
            <table className="cl-styled-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "center", width: "36px" }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredDeals.length && filteredDeals.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(filteredDeals.map((d) => d.id)));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                    />
                  </th>
                  <th>Property Address</th>
                  <th>Status</th>
                  <th>Program Status</th>
                  <th>Realtor</th>
                  <th style={{ textAlign: "center" }}>Milestones</th>
                  <th>Created</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeals.map((deal) => {
                  const eligible = isResetEligible(deal);
                  return <tr key={deal.id}>
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(deal.id)}
                        onChange={() => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(deal.id)) {
                              next.delete(deal.id);
                            } else {
                              next.add(deal.id);
                            }
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td style={{ fontWeight: 500, color: "#333" }}>
                      <Link
                        to={`/admin/hitl/deals/all/${deal.id}`}
                        style={{ color: "inherit" }}
                      >
                        {deal.display_address || deal.property_address || "—"}
                      </Link>
                    </td>
                    <td>
                      <span className={STATUS_CLASS[deal.status] || ""}>
                        {STATUS_LABELS[deal.status] || deal.status}
                      </span>
                    </td>
                    <td>
                      {deal.program_status
                        ? deal.program_status.replace(/_/g, " ")
                        : "—"}
                    </td>
                    <td>{deal.realtor_email}</td>
                    <td style={{ textAlign: "center" }}>{deal.milestone_count}</td>
                    <td>{formatDate(deal.created_at)}</td>
                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <button
                        onClick={() => handleCancel(deal)}
                        disabled={deal.status === "cancelled" || deal.status === "closed"}
                        style={{
                          marginRight: "8px",
                          padding: "4px 10px",
                          fontSize: "12px",
                          borderRadius: "4px",
                          border: "1px solid #bbb",
                          background:
                            deal.status === "cancelled" || deal.status === "closed"
                              ? "#f5f5f5"
                              : "#fff",
                          color:
                            deal.status === "cancelled" || deal.status === "closed"
                              ? "#bbb"
                              : "#555",
                          cursor:
                            deal.status === "cancelled" || deal.status === "closed"
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleReset(deal)}
                        disabled={!eligible}
                        style={{
                          marginRight: "8px",
                          padding: "4px 10px",
                          fontSize: "12px",
                          borderRadius: "4px",
                          border: "1px solid #bbb",
                          background: eligible ? "#fff" : "#f5f5f5",
                          color: eligible ? "#555" : "#bbb",
                          cursor: eligible ? "pointer" : "not-allowed",
                        }}
                      >
                        Reset
                      </button>
                      <button
                        onClick={() => handleDelete(deal)}
                        style={{
                          padding: "4px 10px",
                          fontSize: "12px",
                          borderRadius: "4px",
                          border: "1px solid #e57373",
                          background: "#fff",
                          color: "#c62828",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>;
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const filterInputStyle = {
  padding: "6px 10px",
  fontSize: "13px",
  border: "1px solid #d0d0d0",
  borderRadius: "4px",
  outline: "none",
  minWidth: "160px",
  color: "#333",
  backgroundColor: "#fff",
};

export default AdminDealList;
